import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { toCanvas } from 'html-to-image';
import { Subject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { WidgetPredictiveOptions } from '../widget-predictive/widget-predictive.component';
import { IExportCanvas } from '../../models/export-canvas';
import { Breakdown, Driver } from '../../models/predictive.models';
import {
  QuadChartDirector,
  QuadChartPointCustomData,
} from '../../services/highcharts/quad-chart/quad-chart.director';
import { SmartSummaryService } from '../../services/smart-summary.service';
import { PointPopoutComponent } from './point-popout/point-popout.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-predictive-chart-widgetette',
  imports: [AppSharedModule, PointPopoutComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './predictive-chart-widgetette.component.html',
  styleUrls: ['./predictive-chart-widgetette.component.scss'],
})
export class PredictiveChartWidgetetteComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy, IExportCanvas
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() breakdown!: Breakdown;
  @Input() predictiveOptions!: WidgetPredictiveOptions;
  @Input() isReadOnly!: boolean;
  @Input() isDashboardLocked!: boolean;
  @Input() hoveredDriver?: Driver | null;

  @Output() smartSummary = new EventEmitter<Driver>();

  @ViewChild('chart', { static: false })
  chartElement: ElementRef<HTMLDivElement>;

  private quadChartDirector: QuadChartDirector;
  chart: Highcharts.Chart;

  isPopoutVisible = false;
  pointElement: EventTarget;
  pointDriver: Driver;

  private hasSmartSummaryFeature: boolean;

  private readonly smartSummaryService = inject(SmartSummaryService);

  private readonly destroy$ = new Subject<void>();
  private readonly pointClick$ = new Subject<Highcharts.PointClickEventObject>();

  ngOnChanges(changes: SimpleChanges): void {
    if (PredictiveChartWidgetetteComponent.shouldDrawChart(changes)) {
      this.drawChart();
    }

    if (PredictiveChartWidgetetteComponent.shouldHoverDriver(changes)) {
      this.hoverDriver();
    }
  }

  async ngOnInit(): Promise<void> {
    this.quadChartDirector = new QuadChartDirector({
      plotType: this.predictiveOptions.plotType,
      decimals: parseInt(this.predictiveOptions.decimals, 10),
      displayLabels: this.predictiveOptions.displayLabels,
      showNumericAxisLabels: this.predictiveOptions.showNumericAxisLabels,
      pointClickSubject: this.pointClick$,
    });

    this.pointClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handlePointClick(event));

    this.hasSmartSummaryFeature =
      await this.smartSummaryService.hasPermissionForSummaryByDriverField();
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClosePopout() {
    this.isPopoutVisible = false;
  }

  onSmartSummary(): void {
    this.smartSummary.emit(this.pointDriver);

    this.onClosePopout();
  }

  drawChart(): void {
    const options = this.quadChartDirector.construct({ breakdown: this.breakdown });

    this.chart = Highcharts.chart(this.chartElement.nativeElement, options);
  }

  exportCanvas(): Promise<HTMLCanvasElement> {
    return toCanvas(this.chartElement.nativeElement);
  }

  private static shouldDrawChart(changes: SimpleChanges): boolean {
    return changes.breakdown?.currentValue && !changes.breakdown.firstChange;
  }

  private handlePointClick(event: Highcharts.PointClickEventObject): void {
    const pointCustomData = event.point.options.custom as QuadChartPointCustomData;
    if (!pointCustomData) {
      return;
    }

    if (!this.shouldShowPointPopout()) {
      return;
    }

    this.showPointPopout(event.target, pointCustomData.field);
  }

  private shouldShowPointPopout(): boolean {
    return this.hasSmartSummaryFeature && !this.isReadOnly;
  }

  private showPointPopout(target: EventTarget, fieldName: string): void {
    this.pointDriver = this.breakdown.drivers.find((d) => d.name === fieldName);
    if (!this.pointDriver) {
      return;
    }

    this.isPopoutVisible = true;
    this.pointElement = target;
  }

  private static shouldHoverDriver(changes: SimpleChanges): boolean {
    return changes.hoveredDriver && !changes.hoveredDriver.firstChange;
  }

  private hoverDriver(): void {
    if (!this.chart) {
      return;
    }

    if (!this.hoveredDriver) {
      this.chart.series.forEach((s) => s.setState('normal'));
      this.chart.tooltip.hide();

      return;
    }

    this.chart.series.forEach((s) => {
      if (s.name === this.hoveredDriver.name) {
        s.setState('hover');

        this.chart.tooltip.refresh(s.points);
      } else {
        s.setState('inactive');
      }
    });
  }
}
